import { type KeyboardEvent, type MouseEvent, type ReactElement, useState } from 'react';
import { r, s } from '@/theme/tokens';
import { Box } from '@/ui';
import { Menu, MenuItem } from '@/ui/primitives';
import { resolveFormFieldBackgroundColor } from '@/ui/primitives/formFieldVariants';
import {
  colourSwatchSelectorControlSize,
  colourSwatchSelectorWidth,
} from './colourSwatchSelectorMetrics';

export type ColourSwatchOption<TValue extends string> = {
  value: TValue;
  swatch: string;
};

type ColourSwatchSelectorProps<TValue extends string> = {
  ariaLabel: string;
  disabled?: boolean;
  onChange: (value: TValue) => void;
  options: Array<ColourSwatchOption<TValue>>;
  value: TValue;
};

const swatchSize = '18px';
const swatchRadius = r('sm');
const fieldBackgroundColor = resolveFormFieldBackgroundColor('settings');

const swatchSx = {
  display: 'inline-flex',
  width: swatchSize,
  height: swatchSize,
  minWidth: swatchSize,
  minHeight: swatchSize,
  maxWidth: swatchSize,
  maxHeight: swatchSize,
  flex: '0 0 auto',
  boxSizing: 'border-box',
  borderRadius: swatchRadius,
  border: '1px solid',
  borderColor: 'divider',
} as const;

export const ColourSwatchSelector = <TValue extends string>({
  ariaLabel,
  disabled = false,
  onChange,
  options,
  value,
}: ColourSwatchSelectorProps<TValue>): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedOptionSwatch =
    options.find((option) => option.value === value)?.swatch ?? 'transparent';
  const selectedSwatch = { backgroundColor: selectedOptionSwatch, borderColor: 'divider' };

  const handleOpenColorMenu = (event: MouseEvent<HTMLElement>): void => {
    if (disabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseColorMenu = (): void => {
    setAnchorEl(null);
  };

  const handleColorKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (disabled) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        onClick={handleOpenColorMenu}
        onKeyDown={handleColorKeyDown}
        sx={{
          minWidth: colourSwatchSelectorWidth,
          width: colourSwatchSelectorWidth,
          maxWidth: colourSwatchSelectorWidth,
          minHeight: colourSwatchSelectorControlSize,
          height: colourSwatchSelectorControlSize,
          maxHeight: colourSwatchSelectorControlSize,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: r('sm'),
          backgroundColor: fieldBackgroundColor,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Box component="span" sx={[swatchSx, selectedSwatch]} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseColorMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: colourSwatchSelectorWidth,
              width: colourSwatchSelectorWidth,
              maxWidth: colourSwatchSelectorWidth,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${r('sm')} !important`,
              backgroundColor: fieldBackgroundColor,
              backgroundImage: 'none',
              boxShadow: 'none',
              overflow: 'hidden',
              clipPath: `inset(0 round ${r('sm')})`,
            },
          },
          list: {
            sx: {
              p: s('none'),
              m: s('none'),
              backgroundColor: fieldBackgroundColor,
            },
          },
        }}
      >
        {options.map((option) => {
          const optionSwatch = { backgroundColor: option.swatch, borderColor: 'divider' };
          return (
            <MenuItem
              key={option.value}
              onClick={() => {
                onChange(option.value);
                handleCloseColorMenu();
              }}
              sx={{
                minHeight: '28px',
                height: '28px',
                px: s('md'),
                py: s('none'),
                justifyContent: 'center',
                borderRadius: r('none'),
                backgroundColor: value === option.value ? 'action.selected' : undefined,
              }}
            >
              <Box component="span" sx={[swatchSx, optionSwatch]} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
